import { formatISO, parseISO } from 'date-fns';
import request from 'utils/request';
import { DictionaryDto, initDictItem, PagedView } from './dictionary';
import { UserListDto } from './user';

export interface CourierDto {
    id: number;
    date_created: Date;
    last_auth_date: Date;
    username: string;
    enabled: boolean;
    first_name: string;
    last_name: string;
    middle_name: string;
    phone_number: string;
    post_box_number: string;
    order_count?: number;
    idn: string;
    email: string;
    status: string;
    region: DictionaryDto;
    is_completed: boolean;
}

export interface Courier extends Omit<CourierDataForm, 'document_type_id'> {
    id: number;
    user: CourierDto;
    type: string;
    is_online: false;
    personnel_number: string;
    pus_username?: string;
    is_beneficiary: boolean;
    card_number?: string;
    fact_address?: string;
    colvir_id?: string;
    document_type: DictionaryDto | null | undefined;
}

export interface CourierDetailsDto {
    courier: Courier;
    courier_assets_group: CourierAssetsGroup;
}

export interface CourierAssetsGroup {
    photos: Photo[];
}

export interface Photo {
    id: number;
    asset_id: number;
    type: string;
}

export interface CourierCoord {
    id: number;
    latitude?: number;
    longitude?: number;
}

export interface CourierDataForm {
    document_type_id: string | number | undefined;
    passport_number: string;
    passport_date: Date | null;
    passport_issued_by: string;
}

export interface CourierEditForm extends Omit<CourierDataForm, 'document_type_id'> {
    card_number?: string;
    fact_address?: string;
    document_type: DictionaryDto | null | undefined;
}

export function initCourierForm(data: Courier | undefined = undefined): CourierEditForm {
    if (data) {
        return {
            card_number: data.card_number || '',
            document_type: data.document_type || initDictItem(),
            fact_address: data.fact_address || '',
            passport_date: data.passport_date ? parseISO(data.passport_date?.toString()) : null,
            passport_issued_by: data.passport_issued_by || '',
            passport_number: data.passport_number || '',
        };
    }

    return {
        card_number: '',
        document_type: initDictItem(),
        fact_address: '',
        passport_date: null,
        passport_issued_by: '',
        passport_number: '',
    };
}

export enum CourierRegType {
    New,
    Confirmed,
    Activation,
}

export function activateCourier(id: number | string, personnelNumber: string): Promise<any> {
    return request({
        url: `/courier/activate?user_id=${id}&personnel_number=${personnelNumber}`,
        method: 'POST',
    });
}

export function confirmCourier(data: CourierDataForm & { user_id: string }): Promise<any> {
    return request({
        url: '/courier/confirm',
        method: 'POST',
        params: {
            ...data,
            ...{
                passport_date: data.passport_date
                    ? formatISO(data.passport_date, { representation: 'date' })
                    : null,
            },
        },
    });
}

export function rejectCourier(id: number | string): Promise<any> {
    return request({
        url: `/courier/reject?user_id=${id}`,
        method: 'POST',
    });
}

export function fetchActiveCouriers(
    regionCode: string | null
): Promise<PagedView<UserListDto | CourierDto>> {
    return request('/courier/active-list', {
        params: {
            region_code: regionCode,
        },
    }).then(({ data }) => data as PagedView<UserListDto | CourierDto>);
}

export function fetchStaffActiveCouriers(
    regionCode: string | null,
    productCode?: string | null,
    branchCode?: string | null
): Promise<PagedView<UserListDto>> {
    return request('/courier/staff-courier/active-list', {
        params: {
            region_code: regionCode,
            product_code: productCode,
            branch_code: branchCode
        },
    }).then(({ data }) => data as PagedView<UserListDto>);
}

export function fetchCourierCoords(regionId: number | string): Promise<CourierCoord[]> {
    return request(`/couriers/locations?regionId=${regionId}`).then(
        (res) => res.data as CourierCoord[]
    );
}

export function fetchNewCourierCount(url: string): Promise<number> {
    return request(`${url}?page=0&size=1`).then(
        ({ data }) => (data as PagedView<CourierDto>).total_elements
    );
}

export function updateCourierData(courierId: number | string, data: any): Promise<any> {
    console.log(formatISO(data.passport_date, { representation: 'date' }));
    return request(`/courier/information/${courierId}`, {
        data: {
            ...data,
            ...{
                passport_date: data.passport_date
                    ? formatISO(data.passport_date, { representation: 'date' })
                    : null,
            },
        },
        method: 'PUT',
    });
}

export function downloadCourierDoc(userId: number | string): Promise<any> {
    return request(`/courier/courier-doc/${userId}`, {
        responseType: 'arraybuffer',
    });
}

export type CourierOrderParam = {
    page: number;
    size: number;
    order_status_id: number | string;
    region_id: number | string;
    product_id: number | string;
    value: string | null | undefined;
};

export function fetchCouriersByOrderCount(data: CourierOrderParam): Promise<PagedView<CourierDto>> {
    return request('order/count-by-couriers', {
        params: data,
    }).then(({ data }) => data as PagedView<CourierDto>);
}
/*
*
* url: "/v1/order/count-by-couriers"
 method: GET
 params: page, size, order_status_id, region_id, product_id, value
 response: Page<CourierDTO>*/
